
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' }
];

interface LanguageSelectorProps {
  scrolled?: boolean;
}

/**
 * Language selector component.
 * Handles click‑outside detection, language switching and basic error handling.
 */
export default function LanguageSelector({ scrolled = false }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fallback to first language if the current i18n language is not in the list.
  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) ?? languages[0];

  /** Close dropdown when clicking outside of it */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /** Change language safely – catches possible i18n errors */
  const changeLanguage = async (code: string) => {
    try {
      await i18n.changeLanguage(code);
    } catch (err) {
      console.error('Failed to change language:', err);
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
          scrolled ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-white/10'
        }`}
        aria-expanded={isOpen}
        aria-label="Select language"
      >
        <span className="text-base">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.code.toUpperCase()}</span>
        <i
          className={`ri-arrow-down-s-line text-sm transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        ></i>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-100 py-2 min-w-[160px] z-50 animate-fade-in">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                currentLanguage.code === lang.code
                  ? 'bg-gray-50 text-black font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
              {currentLanguage.code === lang.code && (
                <i className="ri-check-line ml-auto text-black"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
